import { useCallback, useEffect, useMemo, useState } from "react";
import {
  CheckCircle2,
  Clock,
  RefreshCw,
  Search,
  XCircle,
  Zap,
  MessageSquare,
  CalendarDays,
  User,
} from "lucide-react";
import {
  decideWfApproval,
  fetchWfApprovals,
  submitOtRequest,
} from "../../../Moduels/attendance/wfApi";
import { resolveEmployeeDisplay } from "../../common/displayUtils";
import EmployeeSelect from "../../common/EmployeeSelect";
import DatePickerField from "../../common/DatePickerField";
import { useEmployees } from "../../../hooks/useEmployees";

const STATUS_TABS = [
  { id: "pending", label: "Pending" },
  { id: "approved", label: "Approved" },
  { id: "rejected", label: "Rejected" },
  { id: "", label: "All" },
];

const TYPE_TABS = [
  { id: "ot_request", label: "OT Requests" },
  { id: "", label: "All types" },
];

const ENTITY_LABELS = {
  ot_request: "OT Request",
  regularization: "Regularization",
  punch_correction: "Punch Correction",
  attendance_dispute: "Attendance Dispute",
  missing_punch: "Missing Punch",
  shift_swap: "Shift Swap",
};

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(`${String(value).slice(0, 10)}T00:00:00`);
  return d.toLocaleDateString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function formatDateTime(value) {
  if (!value) return "—";
  return new Date(value).toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function statusBadge(status) {
  const s = String(status || "").toLowerCase();
  if (s === "approved") {
    return "bg-emerald-50 text-emerald-800 border-emerald-200";
  }
  if (s === "rejected") {
    return "bg-rose-50 text-rose-800 border-rose-200";
  }
  return "bg-amber-50 text-amber-800 border-amber-200";
}

function extractOtDetails(row) {
  const payload = row?.payload_json || {};
  return {
    workDate: payload.work_date || payload.workDate || "—",
    hours: payload.hours ?? payload.ot_hours ?? "—",
    reason: payload.reason || payload.notes || "—",
  };
}

function MetricCard({ icon: Icon, label, value, hint, tone = "default" }) {
  const tones = {
    default: "border-slate-200 bg-white",
    warn: "border-amber-200 bg-amber-50/50",
    ok: "border-emerald-200 bg-emerald-50/50",
  };
  return (
    <div className={`rounded-xl border p-4 ${tones[tone] || tones.default}`}>
      <div className="flex items-center gap-2 text-slate-500 text-xs font-semibold uppercase tracking-wide">
        <Icon className="w-4 h-4" />
        {label}
      </div>
      <p className="text-2xl font-bold text-slate-900 mt-2">{value}</p>
      {hint ? <p className="text-xs text-slate-500 mt-1">{hint}</p> : null}
    </div>
  );
}

export default function WfApprovals() {
  const { employees } = useEmployees();
  const [rows, setRows] = useState([]);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [typeFilter, setTypeFilter] = useState("ot_request");
  const [employeeFilter, setEmployeeFilter] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [decidingId, setDecidingId] = useState(null);
  const [modal, setModal] = useState(null);
  const [comment, setComment] = useState("");
  const [showSubmit, setShowSubmit] = useState(false);
  const [submitForm, setSubmitForm] = useState({
    employee_id: "",
    work_date: "",
    hours: "",
    reason: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setError("");
    setLoading(true);
    try {
      const data = await fetchWfApprovals({
        statusFilter: statusFilter || undefined,
        entityType: typeFilter || undefined,
      });
      setRows(data);
    } catch (e) {
      setError(e.message || "Failed to load OT requests");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, typeFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const filteredRows = useMemo(() => {
    let list = rows;
    if (employeeFilter) {
      list = list.filter((r) => String(r.employee_id) === String(employeeFilter));
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((r) => {
        const name = resolveEmployeeDisplay(r, employees).toLowerCase();
        const reason = String(extractOtDetails(r).reason).toLowerCase();
        return name.includes(q) || reason.includes(q);
      });
    }
    return list;
  }, [rows, employeeFilter, search, employees]);

  const stats = useMemo(() => {
    const pending = rows.filter((r) => r.status === "pending");
    const approved = rows.filter((r) => r.status === "approved");
    const pendingHours = pending.reduce((sum, r) => {
      const h = Number(extractOtDetails(r).hours);
      return sum + (Number.isFinite(h) ? h : 0);
    }, 0);
    return {
      pending: pending.length,
      approved: approved.length,
      pendingHours: pendingHours.toFixed(1),
    };
  }, [rows]);

  const openDecideModal = (row, action) => {
    setModal({ row, action });
    setComment("");
    setError("");
  };

  const closeModal = () => {
    if (decidingId) return;
    setModal(null);
    setComment("");
  };

  const confirmDecide = async () => {
    if (!modal) return;
    const { row, action } = modal;
    const id = row.request_id;
    setDecidingId(id);
    setError("");
    setRows((prev) => prev.filter((r) => r.request_id !== id));
    try {
      await decideWfApproval(id, { action, comment: comment.trim() || undefined });
      setMessage(
        action === "approve"
          ? "OT request approved successfully."
          : "OT request rejected."
      );
      setModal(null);
      setComment("");
    } catch (e) {
      setRows((prev) => {
        if (prev.some((r) => r.request_id === id)) return prev;
        return [row, ...prev];
      });
      setError(e.message || "Action failed");
    } finally {
      setDecidingId(null);
    }
  };

  const handleSubmitOt = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const hours = Number(submitForm.hours);
      if (!submitForm.employee_id || !submitForm.work_date || !hours || hours <= 0) {
        throw new Error("Employee, OT date, and valid hours are required.");
      }
      await submitOtRequest({
        employee_id: submitForm.employee_id,
        work_date: submitForm.work_date,
        hours,
        reason: submitForm.reason || undefined,
      });
      setMessage("OT request submitted.");
      setShowSubmit(false);
      setSubmitForm({ employee_id: "", work_date: "", hours: "", reason: "" });
      await load();
    } catch (err) {
      setError(err.message || "Failed to submit OT request");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-6 w-full max-w-full min-w-0 space-y-6">
      <div className="rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 via-white to-orange-50 p-6 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-semibold mb-3">
              <Zap className="w-3.5 h-3.5" />
              Overtime workflow
            </div>
            <h1 className="text-2xl font-bold text-slate-900">OT Request Approvals</h1>
            <p className="text-sm text-slate-600 mt-2 max-w-2xl leading-relaxed">
              Review employee overtime requests, verify hours and reason, then approve or reject.
              Approved OT is recorded for payroll and attendance reporting.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={load}
              disabled={loading}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </button>
            <button
              type="button"
              onClick={() => setShowSubmit((v) => !v)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-600 text-white text-sm font-medium hover:bg-amber-700"
            >
              <Zap className="w-4 h-4" />
              New OT request
            </button>
          </div>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <MetricCard
          icon={Clock}
          label="Pending requests"
          value={stats.pending}
          hint="Awaiting your decision"
          tone={stats.pending > 0 ? "warn" : "default"}
        />
        <MetricCard
          icon={Zap}
          label="Pending OT hours"
          value={stats.pendingHours}
          hint="Total hours in pending queue"
          tone="warn"
        />
        <MetricCard
          icon={CheckCircle2}
          label="Approved"
          value={stats.approved}
          hint={`In current ${statusFilter || "all"} view`}
          tone="ok"
        />
      </div>

      {showSubmit ? (
        <form
          onSubmit={handleSubmitOt}
          className="bg-white border border-amber-200 rounded-xl p-5 shadow-sm space-y-4"
        >
          <h2 className="text-sm font-semibold text-slate-800">Submit OT request</h2>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <EmployeeSelect
              label="Employee"
              value={submitForm.employee_id}
              onChange={(v) => setSubmitForm((f) => ({ ...f, employee_id: v }))}
              required
            />
            <DatePickerField
              label="OT date"
              value={submitForm.work_date}
              onChange={(v) => setSubmitForm((f) => ({ ...f, work_date: v }))}
              required
            />
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                OT hours <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="0.5"
                step="0.5"
                value={submitForm.hours}
                onChange={(e) => setSubmitForm((f) => ({ ...f, hours: e.target.value }))}
                className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm"
                placeholder="e.g. 2"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Reason</label>
              <input
                type="text"
                value={submitForm.reason}
                onChange={(e) => setSubmitForm((f) => ({ ...f, reason: e.target.value }))}
                className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm"
                placeholder="Project deadline, client visit…"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 rounded-lg bg-amber-600 text-white text-sm font-medium hover:bg-amber-700 disabled:opacity-50"
            >
              {submitting ? "Submitting…" : "Submit request"}
            </button>
            <button
              type="button"
              onClick={() => setShowSubmit(false)}
              className="px-4 py-2 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50"
            >
              Cancel
            </button>
          </div>
        </form>
      ) : null}

      <div className="bg-white border rounded-xl shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-100 space-y-4">
          <div className="flex flex-wrap gap-2">
            {STATUS_TABS.map((tab) => (
              <button
                key={tab.id || "all"}
                type="button"
                onClick={() => setStatusFilter(tab.id)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium border ${
                  statusFilter === tab.id
                    ? "bg-teal-600 text-white border-teal-600"
                    : "bg-white text-slate-600 border-slate-200 hover:border-teal-300"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            {TYPE_TABS.map((tab) => (
              <button
                key={tab.id || "all-types"}
                type="button"
                onClick={() => setTypeFilter(tab.id)}
                className={`px-3 py-1.5 rounded-full text-sm border ${
                  typeFilter === tab.id
                    ? "bg-amber-100 text-amber-900 border-amber-300"
                    : "bg-white text-slate-600 border-slate-200"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <div className="grid gap-3 md:grid-cols-[1fr_220px]">
            <div className="relative">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search employee or reason…"
                className="w-full pl-9 pr-3 py-2.5 border border-slate-200 rounded-lg text-sm"
              />
            </div>
            <EmployeeSelect
              label="Employee filter"
              value={employeeFilter}
              onChange={setEmployeeFilter}
              allowEmpty
              emptyLabel="All employees"
              hideLabel
            />
          </div>
        </div>

        {message ? (
          <div className="mx-4 mt-4 px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm">
            {message}
          </div>
        ) : null}
        {error ? (
          <div className="mx-4 mt-4 px-3 py-2 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-sm">
            {error}
          </div>
        ) : null}

        {loading ? (
          <div className="p-12 text-center text-slate-500 text-sm">Loading OT requests…</div>
        ) : filteredRows.length === 0 ? (
          <div className="p-12 text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-amber-50 text-amber-600 mb-4">
              <Zap className="w-7 h-7" />
            </div>
            <h3 className="text-lg font-semibold text-slate-800">No OT requests found</h3>
            <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto leading-relaxed">
              {statusFilter === "pending"
                ? "When employees submit overtime requests, they will appear here for approval."
                : "No requests match your current filters. Try another status or request type."}
            </p>
            <ul className="text-xs text-slate-400 mt-4 space-y-1 max-w-sm mx-auto text-left">
              <li>• Employees submit OT from self-service (date, hours, reason)</li>
              <li>• Managers and HR approve or reject with optional comments</li>
              <li>• Approved OT flows into attendance and payroll reports</li>
            </ul>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="p-3 text-left font-semibold">Employee</th>
                  <th className="p-3 text-left font-semibold">Type</th>
                  <th className="p-3 text-left font-semibold">OT date</th>
                  <th className="p-3 text-left font-semibold">Hours</th>
                  <th className="p-3 text-left font-semibold">Reason</th>
                  <th className="p-3 text-left font-semibold">Submitted</th>
                  <th className="p-3 text-left font-semibold">Status</th>
                  <th className="p-3 text-right font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row) => {
                  const ot = extractOtDetails(row);
                  const isPending = row.status === "pending";
                  return (
                    <tr key={row.request_id} className="border-t border-slate-100 hover:bg-slate-50/80">
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <span className="inline-flex w-8 h-8 items-center justify-center rounded-full bg-slate-100 text-slate-600">
                            <User className="w-4 h-4" />
                          </span>
                          <span className="font-medium text-slate-800">
                            {resolveEmployeeDisplay(row, employees)}
                          </span>
                        </div>
                      </td>
                      <td className="p-3 text-slate-600">
                        {ENTITY_LABELS[row.entity_type] || row.entity_type}
                      </td>
                      <td className="p-3">
                        <span className="inline-flex items-center gap-1.5 text-slate-700">
                          <CalendarDays className="w-3.5 h-3.5 text-slate-400" />
                          {formatDate(ot.workDate)}
                        </span>
                      </td>
                      <td className="p-3">
                        <span className="inline-flex items-center gap-1 font-semibold text-amber-700">
                          <Zap className="w-3.5 h-3.5" />
                          {ot.hours}h
                        </span>
                      </td>
                      <td className="p-3 text-slate-600 max-w-[220px] truncate" title={ot.reason}>
                        {ot.reason}
                      </td>
                      <td className="p-3 text-slate-500 whitespace-nowrap">
                        {formatDateTime(row.created_at)}
                      </td>
                      <td className="p-3">
                        <span
                          className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold border capitalize ${statusBadge(
                            row.status
                          )}`}
                        >
                          {row.status}
                        </span>
                      </td>
                      <td className="p-3 text-right">
                        {isPending ? (
                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              disabled={decidingId === row.request_id}
                              onClick={() => openDecideModal(row, "approve")}
                              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 disabled:opacity-50"
                            >
                              <CheckCircle2 className="w-3.5 h-3.5" />
                              Approve
                            </button>
                            <button
                              type="button"
                              disabled={decidingId === row.request_id}
                              onClick={() => openDecideModal(row, "reject")}
                              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-rose-200 text-rose-700 text-xs font-semibold hover:bg-rose-50 disabled:opacity-50"
                            >
                              <XCircle className="w-3.5 h-3.5" />
                              Reject
                            </button>
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-slate-900">
              {modal.action === "approve" ? "Approve OT request" : "Reject OT request"}
            </h3>
            <p className="text-sm text-slate-600 mt-2">
              {resolveEmployeeDisplay(modal.row, employees)} ·{" "}
              {formatDate(extractOtDetails(modal.row).workDate)} ·{" "}
              {extractOtDetails(modal.row).hours}h
            </p>
            <label className="block mt-4 text-sm font-medium text-slate-700">
              <span className="inline-flex items-center gap-1.5 mb-1.5">
                <MessageSquare className="w-4 h-4" />
                Comment (optional)
              </span>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={3}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                placeholder="Add a note for the employee or audit trail…"
              />
            </label>
            <div className="flex justify-end gap-2 mt-5">
              <button
                type="button"
                onClick={closeModal}
                disabled={!!decidingId}
                className="px-4 py-2 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDecide}
                disabled={!!decidingId}
                className={`px-4 py-2 rounded-lg text-white text-sm font-medium disabled:opacity-50 ${
                  modal.action === "approve"
                    ? "bg-emerald-600 hover:bg-emerald-700"
                    : "bg-rose-600 hover:bg-rose-700"
                }`}
              >
                {decidingId ? "Processing…" : modal.action === "approve" ? "Confirm approve" : "Confirm reject"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
